/**
 * V2TemplateTextElement — V2-native template text editor
 *
 * V2 equivalent of TemplateTextElement. Bridges V2 data paths to TiptapTemplateEditor.
 * Supports @links, <tag>labels</tag>, and @flowVariables.
 *
 * Key difference from V1: uses path-based onChange(path, value) instead of
 * returning a full Text object. React 18 batches multiple onChange calls
 * within the same event handler automatically.
 *
 * V2 kind values: 'static' (V1 'default') / 'dynamic' (V1 'template')
 * V2 paths: templateParams.labels/links (V1: parameters.labels/links)
 * V2 translations: translations.{locale} (V1: l10n.{locale})
 *
 * V1 safety: This is a NEW component. V1 code never imports or uses it.
 *
 * @module reactFlow/components/shared/templateFields/V2TemplateTextElement
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Collapse,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { InlineCountdownConfig } from '@segmently/schema-models/v2';
import { TiptapTemplateEditor, type LabelBlockType } from './TiptapTemplateEditor';
import { AddLabelDialog } from './AddLabelDialog';
import { AddLinkDialog, type LinkData } from './AddLinkDialog';
import { AddCountdownDialog } from './AddCountdownDialog';
import { WithGuideHelp } from '@modules/ui-components';
import { StyleFieldRow } from '../styleFields';
import { PaddingElement } from '../../../factories/elements/PaddingElement';
import { useEditorGuideService } from '../../../contexts';
import type { FieldConfig } from '../../../types/editorFieldTypes';
import type { FlowVariableItem } from '../../../config/flowVariablesConfig';
import type { FlowStepNode } from '../../../adapters/V2ScreenAdapter';
import { getV2TranslationValue, getValueByPath } from '../../../utils/v2FieldOperations';
import { getEffectiveStyleValue } from '../../../utils/fieldValueOperations';
import { useEditorPresentationProfile } from '../../onboarding/editorSectionsV2/PresentationProfileContext';
import { getStyleInputTestId, getStyleCheckboxTestId } from '../../../utils/testIdUtils';
import type { PredecessorScreen } from '@modules/reactFlow/types/predecessorScreen.types';
import {
  mainFieldLabelEnabledSx,
  mainFieldLabelDisabledSx,
  checkboxIconSx,
  checkboxCheckedIconSx,
  checkboxSx,
  expandedSectionSx,
  expandedToggleButtonSx,
  expandedToggleButtonActiveSx,
} from '../../../config/editorFieldStyles';
import {
  FIELD_MARGIN_BOTTOM,
  FIELD_GAP,
  FIELD_GAP_SMALL,
  FIELD_STYLE_GRID_GAP,
  FIELD_STYLE_MARGIN_TOP,
} from '../../../config/ui';

/**
 * Props for V2TemplateTextElement
 */
export interface V2TemplateTextElementProps {
  /** V2 StepNode being edited */
  step: FlowStepNode;

  /** Field config with templateConfig (kindPath, labelsPath, linksPath) */
  fieldConfig: FieldConfig;

  /** Current locale */
  locale: string;

  /** V2 path-based onChange */
  onChange: (path: string, value: unknown) => void;

  /** Whether field is enabled (checkbox state) */
  enabled: boolean;

  /** Callback when enabled state changes */
  onEnabledChange: (enabled: boolean) => void;

  /** Whether disabled (saving state) */
  disabled?: boolean;

  /** Screen type for conditional features (e.g., paywall variables) */
  screenType?: string;

  /** Flow variable items (from VariableEditorContext) */
  flowVariableItems?: FlowVariableItem[];

  /** Whether style settings are expanded */
  expandedStyles?: boolean;

  /** Callback when style expansion toggles */
  onStylesExpandToggle?: () => void;

  /** Style values for style fields */
  styleValues?: Record<string, unknown>;

  /** Callback when a style value changes */
  onStyleChange?: (path: string, value: unknown) => void;

  /** Predecessor screens for link dialog dropdown */
  predecessorScreens?: PredecessorScreen[];

  /** Test IDs */
  'data-testid'?: string;
  checkboxTestId?: string;
  stylesToggleTestId?: string;
  inputTestId?: string;
  /** Optional testid for the expanded text-styles grid (used to clip the whole style group). */
  styleGroupTestId?: string;
}

/**
 * V2TemplateTextElement — V2-native wrapper around TiptapTemplateEditor
 *
 * Layout (same as V1 TemplateTextElement):
 * [✓] Label
 * [TiptapEditor_________________] [⚙]
 * ├── [Style fields grid]
 * ├── [Padding (if configured)]
 * ├── Labels section with +ADD
 * └── Links section with +ADD
 */
export function V2TemplateTextElement({
  step,
  fieldConfig,
  locale,
  onChange,
  enabled,
  onEnabledChange,
  disabled = false,
  screenType,
  flowVariableItems,
  expandedStyles = false,
  onStylesExpandToggle,
  styleValues = {},
  onStyleChange,
  predecessorScreens,
  'data-testid': testId,
  checkboxTestId,
  stylesToggleTestId,
  inputTestId,
  styleGroupTestId,
}: V2TemplateTextElementProps): React.ReactElement {
  // Dialog state
  const [addLabelOpen, setAddLabelOpen] = useState(false);
  const [addLinkOpen, setAddLinkOpen] = useState(false);
  const [addCountdownOpen, setAddCountdownOpen] = useState(false);

  // Edit mode state
  const [editingLabelKey, setEditingLabelKey] = useState<string | null>(null);
  const [editingLinkKey, setEditingLinkKey] = useState<string | null>(null);
  const [editingCountdownKey, setEditingCountdownKey] = useState<string | null>(null);
  const [expandedSubsections, setExpandedSubsections] = useState<Set<string>>(new Set());

  const templateConfig = fieldConfig.templateConfig;

  // Read current text value from V2 translations
  const currentText = useMemo(
    () => (enabled ? getV2TranslationValue(step, fieldConfig.l10nPath || fieldConfig.path, locale) : ''),
    [step, fieldConfig, locale, enabled],
  );

  // Read labels and links from V2 templateParams
  const labels = useMemo(
    (): Record<string, LabelBlockType> =>
      templateConfig
        ? getValueByPath<Record<string, LabelBlockType>>(step, templateConfig.labelsPath) || {}
        : {},
    [step, templateConfig],
  );

  const links = useMemo(
    (): Record<string, string> =>
      templateConfig
        ? getValueByPath<Record<string, string>>(step, templateConfig.linksPath) || {}
        : {},
    [step, templateConfig],
  );

  const countdowns = useMemo(
    (): Record<string, InlineCountdownConfig> | undefined =>
      templateConfig?.countdownsPath
        ? getValueByPath<Record<string, InlineCountdownConfig>>(step, templateConfig.countdownsPath) || {}
        : undefined,
    [step, templateConfig],
  );

  // Filter out null entries (deleted via onChange(path, null) but key still present until Firebase sync)
  const labelKeys = Object.keys(labels).filter(k => labels[k] != null);
  const linkKeys = Object.keys(links).filter(k => links[k] != null);
  const countdownKeys = countdowns ? Object.keys(countdowns).filter(k => countdowns[k] != null) : [];

  // Read current kind
  const currentKind = useMemo(
    (): string =>
      templateConfig
        ? getValueByPath<string>(step, templateConfig.kindPath) || 'static'
        : 'static',
    [step, templateConfig],
  );

  const isDynamicMode = currentKind === 'dynamic';

  // Separate style fields from padding fields
  const { textStyleFields, paddingConfig } = useMemo(() => {
    const textFields = (fieldConfig.styleFields || []).filter(
      (f) => !f.path.includes('.padding'),
    );
    return {
      textStyleFields: textFields,
      paddingConfig: fieldConfig.paddingConfig,
    };
  }, [fieldConfig]);

  const hasStyleFields = textStyleFields.length > 0 || !!paddingConfig;

  // Effective values in profile mode: override → base fallback (v2.19.1)
  const activeProfile = useEditorPresentationProfile();

  // Get padding values from step via paths
  const paddingValues = useMemo(() => {
    if (!paddingConfig) return { paddingTop: 0, paddingRight: 0, paddingBottom: 0, paddingLeft: 0 };
    const base = paddingConfig.basePath;
    const stepRecord = step as unknown as Record<string, unknown>;
    return {
      paddingTop: getEffectiveStyleValue<number>(stepRecord, `${base}.top`, activeProfile) ?? 0,
      paddingRight: getEffectiveStyleValue<number>(stepRecord, `${base}.end`, activeProfile) ?? 0,
      paddingBottom: getEffectiveStyleValue<number>(stepRecord, `${base}.bottom`, activeProfile) ?? 0,
      paddingLeft: getEffectiveStyleValue<number>(stepRecord, `${base}.start`, activeProfile) ?? 0,
    };
  }, [step, paddingConfig, activeProfile]);

  // Extract parent text styles from appearance for new tags
  const parentTextStyles = useMemo((): Partial<LabelBlockType> => {
    const basePath = fieldConfig.path;
    return {
      fontFamily: getValueByPath<string>(step, `${basePath}.appearance.fontFamily`),
      fontSize: getValueByPath<number>(step, `${basePath}.appearance.fontSize`),
      fontWeight: getValueByPath<number>(step, `${basePath}.appearance.fontWeight`),
      color: getValueByPath<string>(step, `${basePath}.appearance.color`),
    };
  }, [step, fieldConfig.path]);

  // ── Handlers ─────────────────────────────────

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled) onEnabledChange(event.target.checked);
    },
    [disabled, onEnabledChange],
  );

  // Text change: write to V2 translations path
  const handleTextChange = useCallback(
    (newText: string) => {
      const l10nPath = fieldConfig.l10nPath || fieldConfig.path;
      onChange(`${l10nPath}.translations.${locale}`, newText);
    },
    [onChange, fieldConfig, locale],
  );

  // Auto-switch to 'dynamic' kind helper
  const ensureDynamicKind = useCallback(() => {
    if (templateConfig && currentKind !== 'dynamic') {
      onChange(templateConfig.kindPath, 'dynamic');
    }
  }, [onChange, templateConfig, currentKind]);

  // Handle link add via TiptapTemplateEditor @ menu
  // updatedText contains the text with @linkName already inserted
  const handleAddLinkWithAutoSwitch = useCallback(
    (name: string, url: string, updatedText?: string) => {
      if (!templateConfig) return;

      // Update text (with @linkName)
      if (updatedText !== undefined) {
        const l10nPath = fieldConfig.l10nPath || fieldConfig.path;
        onChange(`${l10nPath}.translations.${locale}`, updatedText);
      }

      // Add link to templateParams
      onChange(`${templateConfig.linksPath}.${name}`, url);

      // Auto-switch kind to 'dynamic'
      ensureDynamicKind();
    },
    [onChange, templateConfig, fieldConfig, locale, ensureDynamicKind],
  );

  // Handle label add via TiptapTemplateEditor BubbleMenu
  // updatedText contains the text with <tag>text</tag> already inserted
  const handleAddLabelWithAutoSwitch = useCallback(
    (name: string, labelBlock: Partial<LabelBlockType>, updatedText?: string) => {
      if (!templateConfig) return;

      // Update text (with <tag>text</tag>)
      if (updatedText !== undefined) {
        const l10nPath = fieldConfig.l10nPath || fieldConfig.path;
        onChange(`${l10nPath}.translations.${locale}`, updatedText);
      }

      // Add label to templateParams
      onChange(`${templateConfig.labelsPath}.${name}`, labelBlock);

      // Auto-switch kind to 'dynamic'
      ensureDynamicKind();
    },
    [onChange, templateConfig, fieldConfig, locale, ensureDynamicKind],
  );

  // Handle countdown add via TiptapTemplateEditor @ menu
  // updatedText contains the text with @timerName already inserted
  const handleAddCountdownWithAutoSwitch = useCallback(
    (name: string, config: InlineCountdownConfig, updatedText?: string) => {
      if (!templateConfig?.countdownsPath) return;

      // Update text (with @timerName)
      if (updatedText !== undefined) {
        const l10nPath = fieldConfig.l10nPath || fieldConfig.path;
        onChange(`${l10nPath}.translations.${locale}`, updatedText);
      }

      // Add countdown to templateParams
      onChange(`${templateConfig.countdownsPath}.${name}`, config);

      // Auto-switch kind to 'dynamic'
      ensureDynamicKind();
    },
    [onChange, templateConfig, fieldConfig, locale, ensureDynamicKind],
  );

  // Handle countdown delete via TiptapTemplateEditor BubbleMenu
  const handleDeleteCountdownFromEditor = useCallback(
    (name: string, updatedText: string) => {
      if (!templateConfig?.countdownsPath) return;

      // Update text (countdown node removed)
      const l10nPath = fieldConfig.l10nPath || fieldConfig.path;
      onChange(`${l10nPath}.translations.${locale}`, updatedText);

      // Remove countdown — set to null to delete from Firebase
      onChange(`${templateConfig.countdownsPath}.${name}`, null);
    },
    [onChange, templateConfig, fieldConfig, locale],
  );

  // Handle link delete via TiptapTemplateEditor BubbleMenu
  const handleDeleteLinkFromEditor = useCallback(
    (name: string, updatedText: string) => {
      if (!templateConfig) return;

      // Update text (link node removed)
      const l10nPath = fieldConfig.l10nPath || fieldConfig.path;
      onChange(`${l10nPath}.translations.${locale}`, updatedText);

      // Remove link — set to null to delete from Firebase
      onChange(`${templateConfig.linksPath}.${name}`, null);
    },
    [onChange, templateConfig, fieldConfig, locale],
  );

  // Handle label delete via TiptapTemplateEditor BubbleMenu
  const handleDeleteLabelFromEditor = useCallback(
    (name: string, updatedText: string) => {
      if (!templateConfig) return;

      // Update text (tag mark removed, text stays)
      const l10nPath = fieldConfig.l10nPath || fieldConfig.path;
      onChange(`${l10nPath}.translations.${locale}`, updatedText);

      // Remove label — set to null to delete from Firebase
      onChange(`${templateConfig.labelsPath}.${name}`, null);
    },
    [onChange, templateConfig, fieldConfig, locale],
  );

  // Handle label add/edit from settings panel dialog
  const handleAddLabel = useCallback(
    (name: string, labelBlock: Partial<LabelBlockType>) => {
      if (!templateConfig) return;

      // If editing and name changed, remove old key
      if (editingLabelKey && editingLabelKey !== name) {
        onChange(`${templateConfig.labelsPath}.${editingLabelKey}`, null);
      }

      onChange(`${templateConfig.labelsPath}.${name}`, labelBlock);
      setEditingLabelKey(null);
    },
    [onChange, templateConfig, editingLabelKey],
  );

  // Handle label delete from settings panel
  const handleDeleteLabel = useCallback(
    (name: string) => {
      if (!templateConfig) return;
      onChange(`${templateConfig.labelsPath}.${name}`, null);
    },
    [onChange, templateConfig],
  );

  // Handle link add/edit from settings panel dialog
  const handleAddLink = useCallback(
    (name: string, linkData: LinkData) => {
      if (!templateConfig) return;

      // If editing and name changed, remove old key
      if (editingLinkKey && editingLinkKey !== name) {
        onChange(`${templateConfig.linksPath}.${editingLinkKey}`, null);
      }

      onChange(`${templateConfig.linksPath}.${name}`, linkData.url);
      setEditingLinkKey(null);
    },
    [onChange, templateConfig, editingLinkKey],
  );

  // Handle link delete from settings panel
  const handleDeleteLink = useCallback(
    (name: string) => {
      if (!templateConfig) return;
      onChange(`${templateConfig.linksPath}.${name}`, null);
    },
    [onChange, templateConfig],
  );

  // Handle countdown add/edit from settings panel dialog
  const handleAddCountdown = useCallback(
    (name: string, config: InlineCountdownConfig) => {
      if (!templateConfig?.countdownsPath) return;

      // If editing and name changed, remove old key
      if (editingCountdownKey && editingCountdownKey !== name) {
        onChange(`${templateConfig.countdownsPath}.${editingCountdownKey}`, null);
      }

      onChange(`${templateConfig.countdownsPath}.${name}`, config);
      ensureDynamicKind();
      setEditingCountdownKey(null);
    },
    [onChange, templateConfig, editingCountdownKey, ensureDynamicKind],
  );

  // Handle countdown delete from settings panel
  const handleDeleteCountdown = useCallback(
    (name: string) => {
      if (!templateConfig?.countdownsPath) return;
      onChange(`${templateConfig.countdownsPath}.${name}`, null);
    },
    [onChange, templateConfig],
  );

  // Dialog open/close handlers
  const handleEditCountdownClick = useCallback((key: string) => {
    setEditingCountdownKey(key);
    setAddCountdownOpen(true);
  }, []);

  const handleCountdownDialogClose = useCallback(() => {
    setAddCountdownOpen(false);
    setEditingCountdownKey(null);
  }, []);

  // Dialog open/close handlers
  const handleEditLabelClick = useCallback((key: string) => {
    setEditingLabelKey(key);
    setAddLabelOpen(true);
  }, []);

  const handleEditLinkClick = useCallback((key: string) => {
    setEditingLinkKey(key);
    setAddLinkOpen(true);
  }, []);

  const handleLabelDialogClose = useCallback(() => {
    setAddLabelOpen(false);
    setEditingLabelKey(null);
  }, []);

  const handleLinkDialogClose = useCallback(() => {
    setAddLinkOpen(false);
    setEditingLinkKey(null);
  }, []);

  // Padding change handler — translates V1 padding keys to V2 EdgeInsets keys
  const handlePaddingChange = useCallback(
    (path: string, value: number) => {
      const v2Path = path
        .replace('.paddingTop', '.top')
        .replace('.paddingRight', '.end')
        .replace('.paddingBottom', '.bottom')
        .replace('.paddingLeft', '.start');
      onChange(v2Path, value);
    },
    [onChange],
  );

  // ── Render ─────────────────────────────────

  // Get guideService from context for WithGuideHelp (null outside EditorGuideProvider)
  const guideService = useEditorGuideService();

  const label = fieldConfig.label;

  return (
    <Box
      data-testid={testId}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: FIELD_GAP_SMALL,
        mb: FIELD_MARGIN_BOTTOM,
      }}
    >
      {/* Label Row: Checkbox + Label */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP_SMALL }} data-testid={checkboxTestId}>
        <Checkbox
          checked={enabled}
          onChange={handleCheckboxChange}
          disabled={disabled}
          size="small"
          icon={<Box sx={checkboxIconSx} />}
          checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
          sx={checkboxSx}
          inputProps={{
            'aria-label': label || 'Enable field',
            'data-testid': checkboxTestId ? `${checkboxTestId}-input` : undefined,
          } as React.InputHTMLAttributes<HTMLInputElement>}
        />
        {label && (
          fieldConfig.guideKey && guideService ? (
            <WithGuideHelp
              guideKey={fieldConfig.guideKey}
              guideService={guideService}
              data-testid={`guide-anchor-${fieldConfig.guideKey}`}
              position="inline-end"
              iconSize="small"
            >
              <Typography
                variant="body2"
                sx={enabled ? mainFieldLabelEnabledSx : mainFieldLabelDisabledSx}
              >
                {label}
              </Typography>
            </WithGuideHelp>
          ) : (
            <Typography
              variant="body2"
              sx={enabled ? mainFieldLabelEnabledSx : mainFieldLabelDisabledSx}
            >
              {label}
            </Typography>
          )
        )}
      </Box>

      {/* Input Row: TiptapEditor + Gear */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP }}>
        <Box sx={{ flex: 1 }}>
          <TiptapTemplateEditor
            value={currentText}
            onChange={handleTextChange}
            disabled={disabled || !enabled}
            placeholder={label || 'Enter text'}
            existingLinks={links}
            existingLabels={labels}
            onAddLink={handleAddLinkWithAutoSwitch}
            onAddLabel={handleAddLabelWithAutoSwitch}
            onDeleteLink={handleDeleteLinkFromEditor}
            onDeleteLabel={handleDeleteLabelFromEditor}
            existingCountdowns={countdowns}
            onAddCountdown={handleAddCountdownWithAutoSwitch}
            onDeleteCountdown={handleDeleteCountdownFromEditor}
            parentTextStyles={parentTextStyles}
            predecessorScreens={predecessorScreens}
            screenType={screenType}
            flowVariables={flowVariableItems}
            data-testid={inputTestId}
          />
        </Box>

        {/* Gear Icon (only if styleFields exist) */}
        {hasStyleFields && (
          <IconButton
            data-testid={stylesToggleTestId || 'style-settings-button'}
            onClick={onStylesExpandToggle}
            size="small"
            disabled={disabled}
            aria-label="Toggle style settings"
            sx={expandedStyles ? expandedToggleButtonActiveSx : expandedToggleButtonSx}
          >
            <TuneIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Style Settings Panel (Expandable) */}
      {hasStyleFields && (
        <Collapse in={expandedStyles} timeout="auto" unmountOnExit>
          <Box
            sx={{
              mt: FIELD_STYLE_MARGIN_TOP,
              ...expandedSectionSx,
            }}
          >
            {/* Style fields grid */}
            {textStyleFields.length > 0 && (
              <Box
                data-testid={styleGroupTestId}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: FIELD_STYLE_GRID_GAP,
                }}
              >
                {textStyleFields.map((styleConfig) => (
                  <StyleFieldRow
                    key={styleConfig.path}
                    config={styleConfig}
                    value={styleValues[styleConfig.path]}
                    onChange={(newValue) => onStyleChange?.(styleConfig.path, newValue)}
                    disabled={disabled || !enabled}
                    inputTestId={getStyleInputTestId(label || 'text', styleConfig.label)}
                    checkboxTestId={getStyleCheckboxTestId(label || 'text', styleConfig.label)}
                  />
                ))}
              </Box>
            )}

            {/* Padding Section */}
            {paddingConfig && (
              <PaddingElement
                basePath={paddingConfig.basePath}
                values={paddingValues}
                onChange={handlePaddingChange}
                disabled={disabled || !enabled}
                label={paddingConfig.label || 'Padding'}
                defaultExpanded={false}
              />
            )}

            {/* Template Settings — single collapsible subsection for Labels, Countdowns, Links */}
            {templateConfig && (
              <Box sx={{ mt: 1 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', py: 0.5 }}
                  onClick={() => setExpandedSubsections(prev => { const n = new Set(prev); n.has('template') ? n.delete('template') : n.add('template'); return n; })}
                >
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{ transform: expandedSubsections.has('template') ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }}
                  />
                  <Typography variant="body2" fontWeight="medium">
                    Template Settings
                  </Typography>
                </Box>
                <Collapse in={expandedSubsections.has('template')}>
                  <Box sx={{ pl: 1 }}>
                    {/* Labels */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          Labels
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => setAddLabelOpen(true)}
                          disabled={disabled || !enabled}
                        >
                          Add
                        </Button>
                      </Box>
                      {labelKeys.length > 0 ? (
                        <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                          {labelKeys.map((key) => (
                            <ListItem
                              key={key}
                              onClick={() => handleEditLabelClick(key)}
                              secondaryAction={
                                <IconButton
                                  edge="end"
                                  aria-label="delete"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteLabel(key); }}
                                  disabled={disabled || !enabled}
                                  size="small"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              }
                              sx={{ py: 0.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                            >
                              <ListItemText
                                primary={key}
                                secondary={`font: ${(labels[key] as Record<string, unknown>)?.fontFamily || 'default'}, ${(labels[key] as Record<string, unknown>)?.fontSize || 16}px`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No labels defined
                        </Typography>
                      )}
                    </Box>

                    {/* Countdowns */}
                    {templateConfig.countdownsPath && (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Countdowns
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => setAddCountdownOpen(true)}
                            disabled={disabled || !enabled}
                          >
                            Add
                          </Button>
                        </Box>
                        {countdownKeys.length > 0 ? (
                          <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                            {countdownKeys.map((key) => (
                              <ListItem
                                key={key}
                                onClick={() => handleEditCountdownClick(key)}
                                secondaryAction={
                                  <IconButton
                                    edge="end"
                                    aria-label="delete"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteCountdown(key); }}
                                    disabled={disabled || !enabled}
                                    size="small"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                }
                                sx={{ py: 0.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                              >
                                <ListItemText
                                  primary={key}
                                  secondary={
                                    countdowns?.[key]
                                      ? `${countdowns[key].mode === 'duration' ? `${countdowns[key].duration}s` : 'target date'} · ${countdowns[key].format}`
                                      : ''
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No countdowns defined
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Links */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          Links
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => setAddLinkOpen(true)}
                          disabled={disabled || !enabled}
                        >
                          Add
                        </Button>
                      </Box>
                      {linkKeys.length > 0 ? (
                        <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                          {linkKeys.map((key) => (
                            <ListItem
                              key={key}
                              onClick={() => handleEditLinkClick(key)}
                              secondaryAction={
                                <IconButton
                                  edge="end"
                                  aria-label="delete"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteLink(key); }}
                                  disabled={disabled || !enabled}
                                  size="small"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              }
                              sx={{ py: 0.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                            >
                              <ListItemText
                                primary={key}
                                secondary={links[key]}
                                secondaryTypographyProps={{ noWrap: true, sx: { maxWidth: 200 } }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No links defined
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            )}
          </Box>
        </Collapse>
      )}

      {/* Add/Edit Label Dialog */}
      <AddLabelDialog
        open={addLabelOpen}
        onClose={handleLabelDialogClose}
        onAdd={handleAddLabel}
        existingKeys={labelKeys}
        mode={editingLabelKey ? 'edit' : 'add'}
        editingKey={editingLabelKey || undefined}
        initialLabelBlock={editingLabelKey ? labels[editingLabelKey] : undefined}
      />

      {/* Add/Edit Link Dialog */}
      <AddLinkDialog
        open={addLinkOpen}
        onClose={handleLinkDialogClose}
        onAdd={handleAddLink}
        existingKeys={linkKeys}
        predecessorScreens={predecessorScreens}
        mode={editingLinkKey ? 'edit' : 'add'}
        editingKey={editingLinkKey || undefined}
        initialUrl={editingLinkKey ? links[editingLinkKey] : undefined}
      />

      {/* Add/Edit Countdown Dialog */}
      {templateConfig?.countdownsPath && (
        <AddCountdownDialog
          open={addCountdownOpen}
          onClose={handleCountdownDialogClose}
          onAdd={handleAddCountdown}
          existingKeys={countdownKeys}
          mode={editingCountdownKey ? 'edit' : 'add'}
          editingKey={editingCountdownKey || undefined}
          initialConfig={editingCountdownKey && countdowns ? countdowns[editingCountdownKey] : undefined}
        />
      )}
    </Box>
  );
}

export default V2TemplateTextElement;
